import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import "@awesome.me/webawesome/dist/components/card/card.js";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/badge/badge.js";
import "@awesome.me/webawesome/dist/components/divider/divider.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "../components/app-shell";
import { getDb, dbManager } from "../db";
import { syncService, type SyncState } from "../services/sync";
import { apiBase } from "../services/api-config";
import { VERSION_INFO } from "../config/version";

interface ServerManifest {
  userId: string;
  version: number;
  lastModified: string;
  etag: string;
  tableVersions: {
    entries: number;
    periods: number;
    settings: number;
    cgmReadings: number;
    dayNotes: number;
  };
  counts: {
    entries: number;
    periods: number;
    cgmReadings: number;
    dayNotes: number;
  };
}

interface LocalCounts {
  entries: number;
  periods: number;
  settings: number;
  cgmReadings: number;
  dayNotes: number;
}

interface LocalVersions {
  entries: number;
  periods: number;
  settings: number;
  cgmReadings: number;
  dayNotes: number;
}

@customElement("diagnostic-page")
export class DiagnosticPage extends LitElement {
  @state() private loading = true;
  @state() private syncing = false;
  @state() private syncMessage = "";
  @state() private syncState: SyncState | null = null;
  @state() private serverManifest: ServerManifest | null = null;
  @state() private serverError = "";
  @state() private localCounts: LocalCounts | null = null;
  @state() private localVersions: LocalVersions | null = null;
  @state() private lastSyncTimestamp = "";
  @state() private pendingItems: { table: string; count: number }[] = [];
  @state() private latestCgmTimestamp = "";
  @state() private latestEntryTimestamp = "";

  private _syncUnsubscribe?: () => void;

  static styles = css`
    :host {
      display: block;
    }

    .diagnostic-content {
      padding: 1rem;
      max-width: 800px;
      margin: 0 auto;
    }

    h2 {
      font-size: 1.1rem;
      font-weight: 600;
      margin: 1.5rem 0 1rem;
      color: #212529;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    h2:first-of-type {
      margin-top: 0;
    }

    wa-card {
      margin-bottom: 1rem;
    }

    wa-card::part(body) {
      padding: 1rem;
    }

    .version-info {
      background: #f8f9fa;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      font-family: monospace;
      font-size: 0.85rem;
      margin-bottom: 1rem;
    }

    .data-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 0.5rem;
    }

    .data-row {
      display: contents;
    }

    .data-label {
      font-weight: 500;
      color: #495057;
      padding: 0.5rem;
      background: #f8f9fa;
      border-radius: 0.25rem;
    }

    .data-value {
      padding: 0.5rem;
      font-family: monospace;
      text-align: right;
      background: #fff;
      border: 1px solid #dee2e6;
      border-radius: 0.25rem;
    }

    .data-value.match {
      background: #d4edda;
      border-color: #28a745;
    }

    .data-value.mismatch {
      background: #f8d7da;
      border-color: #dc3545;
    }

    .data-header {
      font-weight: 600;
      font-size: 0.75rem;
      text-transform: uppercase;
      color: #6c757d;
      padding: 0.5rem;
      text-align: center;
    }

    .sync-status {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      margin-bottom: 1rem;
    }

    .sync-status.idle {
      background: #d4edda;
      color: #155724;
    }

    .sync-status.syncing,
    .sync-status.checking {
      background: #fff3cd;
      color: #856404;
    }

    .sync-status.error {
      background: #f8d7da;
      color: #721c24;
    }

    .sync-status.offline {
      background: #e2e3e5;
      color: #383d41;
    }

    .actions {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-top: 1rem;
    }

    .pending-list {
      font-size: 0.85rem;
      color: #6c757d;
    }

    .pending-list span {
      display: inline-block;
      margin-right: 1rem;
    }

    .timestamp {
      font-family: monospace;
      font-size: 0.85rem;
      color: #6c757d;
    }

    .info-row {
      display: flex;
      justify-content: space-between;
      padding: 0.5rem 0;
      border-bottom: 1px solid #dee2e6;
    }

    .info-row:last-child {
      border-bottom: none;
    }

    .info-label {
      color: #6c757d;
    }

    .info-value {
      font-family: monospace;
      font-weight: 500;
    }

    .loading-container {
      display: flex;
      justify-content: center;
      padding: 2rem;
    }

    wa-callout {
      margin-bottom: 1rem;
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    this._syncUnsubscribe = syncService.subscribe((state) => {
      this.syncState = state;
    });
    await this._loadData();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._syncUnsubscribe?.();
  }

  private async _loadData() {
    this.loading = true;
    this.serverError = "";

    try {
      // Load local data counts
      await this._loadLocalData();

      // Fetch server manifest
      await this._fetchServerManifest();
    } catch (error) {
      console.error("Error loading diagnostic data:", error);
    } finally {
      this.loading = false;
    }
  }

  private async _loadLocalData() {
    if (!dbManager.isInitialized) {
      return;
    }

    const db = getDb();

    // Get counts
    const [entries, periods, settings, cgmReadings, dayNotes] =
      await Promise.all([
        db.entries.count(),
        db.periods.count(),
        db.settings.count(),
        db.cgmReadings.count(),
        db.dayNotes.count(),
      ]);

    this.localCounts = { entries, periods, settings, cgmReadings, dayNotes };

    // Get sync metadata
    const syncMeta = await db.syncMetadata.get("state");
    if (syncMeta) {
      this.lastSyncTimestamp = syncMeta.lastSyncTimestamp || "";
      this.localVersions = {
        entries: syncMeta.tableVersions?.entries || 0,
        periods: syncMeta.tableVersions?.periods || 0,
        settings: syncMeta.tableVersions?.settings || 0,
        cgmReadings: syncMeta.tableVersions?.cgmReadings || 0,
        dayNotes: syncMeta.tableVersions?.dayNotes || 0,
      };
    }

    // Get pending items count per table
    const pendingItems: { table: string; count: number }[] = [];
    for (const table of [
      "entries",
      "periods",
      "settings",
      "cgmReadings",
      "dayNotes",
    ] as const) {
      const count = await (db[table] as any)
        .where("_syncStatus")
        .equals("pending")
        .count();
      if (count > 0) {
        pendingItems.push({ table, count });
      }
    }
    this.pendingItems = pendingItems;

    // Get latest timestamps
    const latestCgm = await db.cgmReadings.orderBy("timestamp").last();
    if (latestCgm) {
      this.latestCgmTimestamp = new Date(latestCgm.timestamp).toLocaleString();
    }

    const latestEntry = await db.entries.orderBy("timestamp").last();
    if (latestEntry) {
      this.latestEntryTimestamp = new Date(
        latestEntry.timestamp,
      ).toLocaleString();
    }
  }

  private async _fetchServerManifest() {
    try {
      const response = await fetch(`${apiBase}/api/sync/manifest`, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      if (response.status === 401) {
        this.serverError = "Not authenticated";
        return;
      }

      if (!response.ok) {
        this.serverError = `Server error: ${response.status}`;
        return;
      }

      this.serverManifest = await response.json();
    } catch (error) {
      this.serverError =
        error instanceof Error ? error.message : "Failed to fetch manifest";
    }
  }

  private async _forceSync() {
    this.syncing = true;
    this.syncMessage = "";

    try {
      const result = await syncService.checkAndSync();
      if (result.success) {
        this.syncMessage = `Sync complete. Pushed: ${result.pushed || 0}, Pulled: ${result.pulled || 0}`;
      } else if (result.skipped) {
        this.syncMessage = "No changes to sync";
      } else {
        this.syncMessage = `Sync failed: ${result.error || "Unknown error"}`;
      }
      // Reload data after sync
      await this._loadData();
    } catch (error) {
      this.syncMessage = `Sync error: ${error instanceof Error ? error.message : "Unknown"}`;
    } finally {
      this.syncing = false;
    }
  }

  private async _forcePull() {
    this.syncing = true;
    this.syncMessage = "Forcing full pull from server...";

    try {
      const db = getDb();
      // Clear local sync metadata to force full sync
      await db.syncMetadata.delete("state");

      // Now sync
      const result = await syncService.checkAndSync();
      if (result.success) {
        this.syncMessage = `Force pull complete. Pulled: ${result.pulled || 0}`;
      } else {
        this.syncMessage = `Force pull failed: ${result.error || "Unknown error"}`;
      }
      await this._loadData();
    } catch (error) {
      this.syncMessage = `Force pull error: ${error instanceof Error ? error.message : "Unknown"}`;
    } finally {
      this.syncing = false;
    }
  }

  private _getSyncStatusClass(): string {
    return this.syncState?.status || "idle";
  }

  private _getSyncStatusText(): string {
    switch (this.syncState?.status) {
      case "syncing":
        return "Syncing...";
      case "checking":
        return "Checking for updates...";
      case "error":
        return `Error: ${this.syncState.error}`;
      case "offline":
        return "Offline";
      default:
        return "Synced";
    }
  }

  private _getComparisonClass(
    local: number | undefined,
    server: number | undefined,
  ): string {
    if (local === undefined || server === undefined) return "";
    return local === server ? "match" : "mismatch";
  }

  render() {
    return html`
      <app-shell pageTitle="Sync Diagnostics" currentPath="/diagnostic">
        <div class="diagnostic-content">
          <!-- Version Info -->
          <div class="version-info">
            Build: ${VERSION_INFO.version} | Date: ${VERSION_INFO.buildDate}
          </div>

          <!-- Sync Status -->
          <div class="sync-status ${this._getSyncStatusClass()}">
            <wa-icon
              name=${this.syncState?.status === "syncing" ||
              this.syncState?.status === "checking"
                ? "arrows-rotate"
                : this.syncState?.status === "error"
                  ? "triangle-exclamation"
                  : this.syncState?.status === "offline"
                    ? "wifi-slash"
                    : "circle-check"}
            ></wa-icon>
            <span>${this._getSyncStatusText()}</span>
            ${this.syncState?.pendingChanges
              ? html`<wa-badge variant="warning"
                  >${this.syncState.pendingChanges} pending</wa-badge
                >`
              : ""}
          </div>

          ${this.syncMessage
            ? html`<wa-callout
                variant=${this.syncMessage.includes("error") ||
                this.syncMessage.includes("failed")
                  ? "danger"
                  : "success"}
                open
              >
                ${this.syncMessage}
              </wa-callout>`
            : ""}

          <!-- Actions -->
          <div class="actions">
            <wa-button
              variant="brand"
              @click=${this._forceSync}
              ?loading=${this.syncing}
              ?disabled=${this.syncing}
            >
              <wa-icon slot="prefix" name="arrows-rotate"></wa-icon>
              Sync Now
            </wa-button>
            <wa-button
              variant="warning"
              @click=${this._forcePull}
              ?loading=${this.syncing}
              ?disabled=${this.syncing}
            >
              <wa-icon slot="prefix" name="cloud-arrow-down"></wa-icon>
              Force Full Pull
            </wa-button>
            <wa-button @click=${this._loadData} ?disabled=${this.loading}>
              <wa-icon slot="prefix" name="arrows-rotate"></wa-icon>
              Refresh
            </wa-button>
          </div>

          ${this.loading
            ? html`<div class="loading-container">
                <wa-spinner style="font-size: 2rem;"></wa-spinner>
              </div>`
            : html`
                <!-- Data Comparison -->
                <h2>
                  <wa-icon name="database"></wa-icon>
                  Data Counts
                </h2>

                ${this.serverError
                  ? html`<wa-callout variant="danger" open>
                      Server: ${this.serverError}
                    </wa-callout>`
                  : ""}

                <wa-card>
                  <div class="data-grid">
                    <div class="data-header">Table</div>
                    <div class="data-header">Local</div>
                    <div class="data-header">Server</div>

                    <div class="data-row">
                      <div class="data-label">CGM Readings</div>
                      <div
                        class="data-value ${this._getComparisonClass(
                          this.localCounts?.cgmReadings,
                          this.serverManifest?.counts.cgmReadings,
                        )}"
                      >
                        ${this.localCounts?.cgmReadings ?? "-"}
                      </div>
                      <div class="data-value">
                        ${this.serverManifest?.counts.cgmReadings ?? "-"}
                      </div>
                    </div>

                    <div class="data-row">
                      <div class="data-label">Diary Entries</div>
                      <div
                        class="data-value ${this._getComparisonClass(
                          this.localCounts?.entries,
                          this.serverManifest?.counts.entries,
                        )}"
                      >
                        ${this.localCounts?.entries ?? "-"}
                      </div>
                      <div class="data-value">
                        ${this.serverManifest?.counts.entries ?? "-"}
                      </div>
                    </div>

                    <div class="data-row">
                      <div class="data-label">Periods</div>
                      <div
                        class="data-value ${this._getComparisonClass(
                          this.localCounts?.periods,
                          this.serverManifest?.counts.periods,
                        )}"
                      >
                        ${this.localCounts?.periods ?? "-"}
                      </div>
                      <div class="data-value">
                        ${this.serverManifest?.counts.periods ?? "-"}
                      </div>
                    </div>

                    <div class="data-row">
                      <div class="data-label">Day Notes</div>
                      <div
                        class="data-value ${this._getComparisonClass(
                          this.localCounts?.dayNotes,
                          this.serverManifest?.counts.dayNotes,
                        )}"
                      >
                        ${this.localCounts?.dayNotes ?? "-"}
                      </div>
                      <div class="data-value">
                        ${this.serverManifest?.counts.dayNotes ?? "-"}
                      </div>
                    </div>

                    <div class="data-row">
                      <div class="data-label">Settings</div>
                      <div class="data-value">
                        ${this.localCounts?.settings ?? "-"}
                      </div>
                      <div class="data-value">-</div>
                    </div>
                  </div>
                </wa-card>

                <!-- Version Comparison -->
                <h2>
                  <wa-icon name="clock-rotate-left"></wa-icon>
                  Table Versions
                </h2>
                <wa-card>
                  <div class="data-grid">
                    <div class="data-header">Table</div>
                    <div class="data-header">Local Ver</div>
                    <div class="data-header">Server Ver</div>

                    <div class="data-row">
                      <div class="data-label">CGM Readings</div>
                      <div
                        class="data-value ${this._getComparisonClass(
                          this.localVersions?.cgmReadings,
                          this.serverManifest?.tableVersions.cgmReadings,
                        )}"
                      >
                        ${this.localVersions?.cgmReadings ?? "-"}
                      </div>
                      <div class="data-value">
                        ${this.serverManifest?.tableVersions.cgmReadings ?? "-"}
                      </div>
                    </div>

                    <div class="data-row">
                      <div class="data-label">Diary Entries</div>
                      <div
                        class="data-value ${this._getComparisonClass(
                          this.localVersions?.entries,
                          this.serverManifest?.tableVersions.entries,
                        )}"
                      >
                        ${this.localVersions?.entries ?? "-"}
                      </div>
                      <div class="data-value">
                        ${this.serverManifest?.tableVersions.entries ?? "-"}
                      </div>
                    </div>

                    <div class="data-row">
                      <div class="data-label">Periods</div>
                      <div
                        class="data-value ${this._getComparisonClass(
                          this.localVersions?.periods,
                          this.serverManifest?.tableVersions.periods,
                        )}"
                      >
                        ${this.localVersions?.periods ?? "-"}
                      </div>
                      <div class="data-value">
                        ${this.serverManifest?.tableVersions.periods ?? "-"}
                      </div>
                    </div>

                    <div class="data-row">
                      <div class="data-label">Settings</div>
                      <div
                        class="data-value ${this._getComparisonClass(
                          this.localVersions?.settings,
                          this.serverManifest?.tableVersions.settings,
                        )}"
                      >
                        ${this.localVersions?.settings ?? "-"}
                      </div>
                      <div class="data-value">
                        ${this.serverManifest?.tableVersions.settings ?? "-"}
                      </div>
                    </div>

                    <div class="data-row">
                      <div class="data-label">Day Notes</div>
                      <div
                        class="data-value ${this._getComparisonClass(
                          this.localVersions?.dayNotes,
                          this.serverManifest?.tableVersions.dayNotes,
                        )}"
                      >
                        ${this.localVersions?.dayNotes ?? "-"}
                      </div>
                      <div class="data-value">
                        ${this.serverManifest?.tableVersions.dayNotes ?? "-"}
                      </div>
                    </div>
                  </div>
                </wa-card>

                <!-- Sync Metadata -->
                <h2>
                  <wa-icon name="circle-info"></wa-icon>
                  Sync Info
                </h2>
                <wa-card>
                  <div class="info-row">
                    <span class="info-label">Last Sync</span>
                    <span class="info-value"
                      >${this.lastSyncTimestamp
                        ? new Date(this.lastSyncTimestamp).toLocaleString()
                        : "Never"}</span
                    >
                  </div>
                  <div class="info-row">
                    <span class="info-label">Server Last Modified</span>
                    <span class="info-value"
                      >${this.serverManifest?.lastModified
                        ? new Date(
                            this.serverManifest.lastModified,
                          ).toLocaleString()
                        : "-"}</span
                    >
                  </div>
                  <div class="info-row">
                    <span class="info-label">Server ETag</span>
                    <span class="info-value"
                      >${this.serverManifest?.etag || "-"}</span
                    >
                  </div>
                  <div class="info-row">
                    <span class="info-label">Latest CGM Reading</span>
                    <span class="info-value"
                      >${this.latestCgmTimestamp || "None"}</span
                    >
                  </div>
                  <div class="info-row">
                    <span class="info-label">Latest Diary Entry</span>
                    <span class="info-value"
                      >${this.latestEntryTimestamp || "None"}</span
                    >
                  </div>
                  ${this.pendingItems.length > 0
                    ? html`
                        <div class="info-row">
                          <span class="info-label">Pending Changes</span>
                          <span class="pending-list">
                            ${this.pendingItems.map(
                              (p) => html`<span>${p.table}: ${p.count}</span>`,
                            )}
                          </span>
                        </div>
                      `
                    : ""}
                </wa-card>

                <!-- Database Info -->
                <h2>
                  <wa-icon name="server"></wa-icon>
                  Database Info
                </h2>
                <wa-card>
                  <div class="info-row">
                    <span class="info-label">Database Initialized</span>
                    <span class="info-value"
                      >${dbManager.isInitialized ? "Yes" : "No"}</span
                    >
                  </div>
                  <div class="info-row">
                    <span class="info-label">User ID</span>
                    <span class="info-value"
                      >${this.serverManifest?.userId || "-"}</span
                    >
                  </div>
                  <div class="info-row">
                    <span class="info-label">API Base</span>
                    <span class="info-value">${apiBase}</span>
                  </div>
                </wa-card>
              `}
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "diagnostic-page": DiagnosticPage;
  }
}
